/**
 * The room: one header, four tabs, and whatever the tab renders.
 *
 * ============================== ONE SHELL, THREE ROOMS =========================
 * Every room has the SAME four tabs in the SAME order — Play · Train · Learn ·
 * Compete — because a player who learns the poker room has learned the blackjack room.
 * Stats is not among them: it moved to the profile (master plan §4, decision 1), and
 * what is left of it in a room is the headline number, which is in the header on every
 * screen so it is never something you have to go and look at.
 *
 * The tab is in the URL — `/poker/play`, `/poker/train` — and not in state. Three
 * reasons, all of them things state cannot do: a tab can be linked to (the saloon's
 * doors open on Play, the chalkboard opens on Compete), a tab survives a reload, and
 * the back button walks back through tabs the way a player expects.
 *
 * The header is also the room's own navigation, because on these screens the site's
 * nav bar is not rendered at all — see `App.tsx`. Back goes to the saloon, the gear
 * opens the Settings sheet, and the `right` slot is for the table, which puts Leave and
 * the units toggle there instead of tabs.
 * ===============================================================================
 */
import { useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import { ROOMS, TABS, roomOf, type Room } from "./rooms";
import { SettingsSheet } from "../settings/SettingsSheet";
import { WalletGauge } from "../lib/WalletContext";
import { Barkeep } from "./Barkeep";
import "./rooms.css";

/**
 * The bar across the top of every room screen, including the table.
 *
 * `--room-accent` is set here and read by everything below it, so a room's colour is
 * one custom property and never a class per room in the stylesheet.
 */
export function RoomHeader({ room, right, title, subtitle, backTo = "/", backLabel = "Saloon" }: {
  room: Room;
  right?: ReactNode;
  /** What the bold line says. The room's name unless the screen has a better one — the
      table passes the card it was dealt from, because the back link already says
      "Poker" and a header that says it twice is a header with no room for anything. */
  title?: ReactNode;
  subtitle?: ReactNode;
  backTo?: string;
  backLabel?: string;
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [barkeepOpen, setBarkeepOpen] = useState(false);
  return (
    <>
      <header className="room-head" style={{ "--room-accent": `var(${room.accent})` } as React.CSSProperties}>
        <Link className="room-back" to={backTo} aria-label={`Back to the ${backLabel.toLowerCase()}`}>
          <span aria-hidden="true">‹</span> <span className="room-back-label">{backLabel}</span>
        </Link>
        <div className="room-title">
          <b>{title ?? room.name}</b>
          {subtitle ? <small>{subtitle}</small> : null}
        </div>
        <div className="room-head-right">
          {right}
          {/*
            * THE WALLET GAUGE, and it is the room's headline number now.
            *
            * Phase 2b drew a label and a dash here, because Trainer Rating is the
            * trainer's and there was no wallet — and a number nobody has is a lie that
            * ends up on every screenshot. The wallet is real from this phase, so this
            * is the balance, and below the floor it is also the way to the Barkeep:
            * that is where somebody looks when they have run out, not in a settings
            * sheet. The table's own top bar no longer draws a second one (`chrome`).
            */}
          <WalletGauge className="room-headline" onStake={() => setBarkeepOpen(true)} />
          <button className="room-gear" type="button" aria-label="Settings"
                  aria-expanded={settingsOpen}
                  onClick={() => setSettingsOpen(true)}>⚙</button>
        </div>
      </header>
      {settingsOpen && <SettingsSheet onClose={() => setSettingsOpen(false)} />}
      {barkeepOpen && <Barkeep onClose={() => setBarkeepOpen(false)} />}
    </>
  );
}

/** The four tabs. Rendered by the shell, never by a tab. */
function RoomTabs({ room }: { room: Room }) {
  return (
    <nav className="room-tabs" aria-label={`${room.name} sections`}>
      {TABS.map((t) => (
        <NavLink key={t.id} to={`${room.path}/${t.id}`}
                 className={({ isActive }) => (isActive ? "on" : "")}>
          {t.label}
          {!room.open && <em>Coming</em>}
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * The shell itself, mounted at `/:roomId`.
 *
 * Reading the room from the route parameter rather than from a prop keeps `App.tsx`'s
 * route table to one line per room, and means a fourth room is an entry in `ROOMS` and
 * nothing else.
 */
export function RoomShell({ roomId }: { roomId?: string }) {
  const params = useParams();
  const { pathname } = useLocation();
  const room = roomOf(roomId ?? params.roomId ?? "");
  if (!room) {
    return (
      <div className="room">
        <div className="room-body">
          <p className="muted">There is no room here. <Link to="/">Back to the saloon</Link>.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="room" style={{ "--room-accent": `var(${room.accent})` } as React.CSSProperties}>
      <RoomHeader room={room} />
      <RoomTabs room={room} />
      {/*
        * The trainer is a WORKSPACE and gets a wider column.
        *
        * `app.css` had this as `.shell-wide` on `/train` for exactly the same reason —
        * a drill list, a felt and a range grid side by side do not fit a reading width.
        * The rule moved with the route; the reasoning is unchanged.
        */}
      <div className={"room-body" + (pathname.includes("/train/") ? " room-body-wide" : "")}>
        <Outlet context={room} />
      </div>
    </div>
  );
}

/**
 * A tab that is not built yet.
 *
 * Used by the whole of the cribbage room and by nothing else that is finished. It says
 * what it will be rather than "coming soon", because a player who is told what is
 * coming can decide whether to care.
 */
export function ComingPanel({ title, lines }: { title: string; lines: string[] }) {
  return (
    <section className="panel room-coming">
      <h2>{title}</h2>
      {lines.map((l) => <p key={l} className="muted">{l}</p>)}
    </section>
  );
}

export { ROOMS };
